import React from 'react';
import { SystemService } from 'services';
import { Globals } from 'utils';
import moment from 'moment';

// ============================================================
// Shapes reales del backend (spec C4).
// La interfaz BackupsResponse del service es laxa; tipamos local
// con todos los campos que consumimos.
// ============================================================

type BackupType = 'api' | 'db' | 'rescue' | 'stocks_restore' | 'other' | string;
type SortOrder = 'recent' | 'oldest' | 'larger' | 'smaller';

interface BackupRow {
	filename?: string;
	size_bytes?: number;
	size_mb?: number;
	type?: BackupType;
	created_at?: string;
	description?: string;
}

interface BackupsFull {
	backups?: BackupRow[];
	total_count?: number;
	total_size_mb?: number;
	backups_path?: string;
}

const REFRESH_INTERVAL_MS = 30 * 1000;
const TICK_INTERVAL_MS = 1000;
const SEARCH_DEBOUNCE_MS = 500;

const TYPE_FILTERS: { value: 'all' | BackupType; label: string }[] = [
	{ value: 'all',             label: 'Todos' },
	{ value: 'api',             label: 'API' },
	{ value: 'db',              label: 'DB' },
	{ value: 'rescue',          label: 'Rescue' },
	{ value: 'stocks_restore',  label: 'Stocks Restore' },
	{ value: 'other',           label: 'Otro' },
];

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
	{ value: 'recent',  label: 'Más recientes' },
	{ value: 'oldest',  label: 'Más antiguos' },
	{ value: 'larger',  label: 'Mayor tamaño' },
	{ value: 'smaller', label: 'Menor tamaño' },
];

// "ok" -> verde, "warning" -> amarillo, "critical" -> rojo, otro -> gris
const typeClass = (type?: BackupType): string => {
	if (!type) return 'other';
	const t = String(type).toLowerCase();
	if (t === 'api' || t === 'db' || t === 'rescue' || t === 'stocks_restore' || t === 'other') return t;
	return 'other';
}

const formatNumber = (v: any): string => {
	const n = Number(v);
	if (isNaN(n)) return '0';
	return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const formatMb = (mb?: number): string => {
	const v = Number(mb);
	if (!isFinite(v)) return '—';
	if (v >= 1024) return `${ (v / 1024).toFixed(2) } GB`;
	return `${ v.toFixed(2) } MB`;
}

const humanizeAgo = (iso?: string | null, now: number = Date.now()): string => {
	if (!iso) return '—';
	const t = moment(iso);
	if (!t.isValid()) return '—';
	const diff = Math.floor((now - t.valueOf()) / 1000);
	if (diff < 0) return 'ahora';
	if (diff < 5) return 'hace instantes';
	if (diff < 60) return `hace ${ diff } seg`;
	const mins = Math.floor(diff / 60);
	if (mins < 60) return `hace ${ mins } min`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `hace ${ hours }h`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `hace ${ days }d`;
	const months = Math.floor(days / 30);
	if (months < 12) return `hace ${ months } mes${ months === 1 ? '' : 'es' }`;
	const years = Math.floor(months / 12);
	return `hace ${ years } año${ years === 1 ? '' : 's' }`;
}

interface State {
	data: BackupsFull | null;
	loadError: boolean;
	initialLoading: boolean;
	lastUpdated: Date | null;
	tick: number;
	// filtros
	typeFilter: 'all' | BackupType;
	search: string;
	searchApplied: string;
	sort: SortOrder;
	// descarga
	downloading: Record<string, boolean>;
}

class Backups extends React.Component<{}, State> {

	refreshTimer: any = null;
	tickTimer: any = null;
	searchDebounceTimer: any = null;
	isMounted_: boolean = false;

	state: State = {
		data: null,
		loadError: false,
		initialLoading: true,
		lastUpdated: null,
		tick: Date.now(),
		typeFilter: 'all',
		search: '',
		searchApplied: '',
		sort: 'recent',
		downloading: {},
	}

	componentDidMount() {
		this.isMounted_ = true;
		this.load(true);

		this.refreshTimer = setInterval(() => this.load(false), REFRESH_INTERVAL_MS);
		this.tickTimer = setInterval(() => {
			if (this.isMounted_) {
				this.setState({ tick: Date.now() });
			}
		}, TICK_INTERVAL_MS);
	}

	componentWillUnmount() {
		this.isMounted_ = false;
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		if (this.tickTimer) clearInterval(this.tickTimer);
		if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
		this.refreshTimer = null;
		this.tickTimer = null;
		this.searchDebounceTimer = null;
	}

	load = async (isFirst: boolean) => {
		try {
			const res: any = await SystemService.admin.getBackups({ withoutLoading: true });
			if (!this.isMounted_) return;
			this.setState({
				data: res || null,
				loadError: false,
				lastUpdated: new Date(),
				initialLoading: isFirst ? false : this.state.initialLoading,
			});
		}
		catch (e) {
			if (!this.isMounted_) return;
			this.setState({
				loadError: true,
				initialLoading: isFirst ? false : this.state.initialLoading,
			});
		}
	}

	manualRefresh = () => {
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		this.load(false);
		this.refreshTimer = setInterval(() => this.load(false), REFRESH_INTERVAL_MS);
	}

	// ---------- Handlers ----------

	switchType = (type: 'all' | BackupType) => {
		if (type === this.state.typeFilter) return;
		this.setState({ typeFilter: type });
	}

	onSearchChange = (value: string) => {
		this.setState({ search: value });
		if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
		this.searchDebounceTimer = setTimeout(() => {
			if (!this.isMounted_) return;
			this.setState({ searchApplied: value });
		}, SEARCH_DEBOUNCE_MS);
	}

	onSortChange = (sort: SortOrder) => {
		this.setState({ sort });
	}

	download = async (filename?: string) => {
		if (!filename) return;
		if (this.state.downloading[filename]) return;

		this.setState((s) => ({
			downloading: { ...s.downloading, [filename]: true },
		}));

		try {
			await SystemService.admin.downloadBackup(filename);
			if (!this.isMounted_) return;
			Globals.showSuccess(`Descarga iniciada: ${ filename }`);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const msg = e?.response?.data?.error || e?.message || 'No se pudo descargar el backup.';
			Globals.showError(msg);
		}
		finally {
			if (!this.isMounted_) return;
			this.setState((s) => {
				const copy = { ...s.downloading };
				delete copy[filename];
				return { downloading: copy };
			});
		}
	}

	// ---------- Derived ----------

	getFilteredSorted = (): BackupRow[] => {
		const list = Array.isArray(this.state.data?.backups) ? this.state.data!.backups as BackupRow[] : [];
		const { typeFilter, searchApplied, sort } = this.state;
		const needle = searchApplied.trim().toLowerCase();

		const filtered = list.filter((b) => {
			if (typeFilter !== 'all' && typeClass(b.type) !== typeFilter) return false;
			if (needle && !(b.filename || '').toLowerCase().includes(needle)) return false;
			return true;
		});

		const sorted = [...filtered];
		sorted.sort((a, b) => {
			if (sort === 'recent' || sort === 'oldest') {
				const ta = a.created_at ? moment(a.created_at).valueOf() : 0;
				const tb = b.created_at ? moment(b.created_at).valueOf() : 0;
				return sort === 'recent' ? tb - ta : ta - tb;
			}
			const sa = Number(a.size_bytes ?? a.size_mb ?? 0);
			const sb = Number(b.size_bytes ?? b.size_mb ?? 0);
			return sort === 'larger' ? sb - sa : sa - sb;
		});

		return sorted;
	}

	// ---------- Render ----------

	renderHeader() {
		const { lastUpdated, initialLoading } = this.state;
		const lastLabel = lastUpdated
			? moment(lastUpdated).utcOffset(-4).format('HH:mm:ss')
			: '—';

		return (
			<div className="backups-header">
				<div className="backups-header-text">
					<h1>Backups</h1>
					<p>Listado y descarga de respaldos del sistema</p>
				</div>
				<div className="backups-header-actions">
					<span className="refresh-badge">
						<span className="refresh-dot"></span>
						Actualizando cada 30s · { lastLabel }
					</span>
					<button
						className="dashboard-refresh-btn"
						onClick={ this.manualRefresh }
						disabled={ initialLoading }>
						{ initialLoading ? 'Cargando…' : 'Refrescar' }
					</button>
				</div>
			</div>
		);
	}

	renderSummary() {
		const { data } = this.state;
		const totalCount = Number(data?.total_count) || 0;
		const totalSize = Number(data?.total_size_mb) || 0;
		const path = data?.backups_path || '—';

		return (
			<div className="backups-summary">
				<div className="backups-summary__item">
					<span className="info-label">Total backups</span>
					<span className="info-value stat">{ formatNumber(totalCount) }</span>
				</div>
				<div className="backups-summary__item">
					<span className="info-label">Tamaño total</span>
					<span className="info-value stat">{ formatMb(totalSize) }</span>
				</div>
				<div className="backups-summary__item backups-summary__item--path">
					<span className="info-label">Path</span>
					<code className="info-value mono ellipsis" title={ path }>{ path }</code>
				</div>
			</div>
		);
	}

	renderFilters() {
		const { typeFilter, search, sort } = this.state;

		return (
			<div className="backups-filters">
				<div className="backup-tabs">
					{
						TYPE_FILTERS.map((t) => (
							<button
								key={ t.value }
								className={ `backup-tab ${ typeFilter === t.value ? 'active' : '' }` }
								onClick={ () => this.switchType(t.value) }>
								{ t.label.toUpperCase() }
							</button>
						))
					}
				</div>
				<div className="backups-filter-controls">
					<div className="logs-filter-field logs-filter-search">
						<label>Buscar</label>
						<div className="logs-search-wrap">
							<span className="logs-search-icon" aria-hidden="true">⌕</span>
							<input
								type="text"
								className="filter-input"
								placeholder="Nombre de archivo…"
								value={ search }
								onChange={ (e) => this.onSearchChange(e.target.value) }
							/>
						</div>
					</div>
					<div className="logs-filter-field">
						<label>Orden</label>
						<select
							className="filter-select"
							value={ sort }
							onChange={ (e) => this.onSortChange(e.target.value as SortOrder) }>
							{ SORT_OPTIONS.map((o) => <option key={ o.value } value={ o.value }>{ o.label }</option>) }
						</select>
					</div>
				</div>
			</div>
		);
	}

	renderTable() {
		const { loadError, initialLoading, downloading, tick } = this.state;

		if (loadError) {
			return (
				<div className="cron-error-card">
					No se pudo cargar el listado de backups. Reintentando en 30s…
				</div>
			);
		}

		const rows = this.getFilteredSorted();

		return (
			<div className="backup-table-wrap">
				<table className="backup-table">
					<thead>
						<tr>
							<th>Archivo</th>
							<th>Tipo</th>
							<th>Tamaño</th>
							<th>Creado</th>
							<th className="actions-col">Acciones</th>
						</tr>
					</thead>
					<tbody>
						{
							rows.map((b, idx) => {
								const cls = typeClass(b.type);
								const created = b.created_at ? moment(b.created_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss') : '—';
								const isDownloading = !!(b.filename && downloading[b.filename]);
								return (
									<tr key={ b.filename || idx }>
										<td className="backup-row__filename">
											<code className="mono ellipsis" title={ b.filename }>{ b.filename || '—' }</code>
											{
												b.description && (
													<span className="backup-row__desc" title={ b.description }>{ b.description }</span>
												)
											}
										</td>
										<td className="backup-row__type">
											<span className={ `backup-type-pill ${ cls }` }>{ (b.type || 'OTHER').toUpperCase() }</span>
										</td>
										<td className="backup-row__size mono">{ formatMb(b.size_mb) }</td>
										<td className="backup-row__created" title={ created }>
											<span className="created-rel">{ humanizeAgo(b.created_at, tick) }</span>
											<span className="created-abs mono">{ created }</span>
										</td>
										<td className="backup-row__actions">
											<button
												className="download-btn"
												onClick={ () => this.download(b.filename) }
												disabled={ isDownloading || !b.filename }>
												{ isDownloading ? (
													<>
														<span className="download-btn__spinner" aria-hidden="true"></span>
														Descargando…
													</>
												) : 'Descargar' }
											</button>
										</td>
									</tr>
								);
							})
						}
						{
							rows.length === 0 && !initialLoading && (
								<tr>
									<td colSpan={ 5 } className="empty-row">
										No hay backups que coincidan con los filtros.
									</td>
								</tr>
							)
						}
						{
							rows.length === 0 && initialLoading && (
								<tr>
									<td colSpan={ 5 } className="empty-row">Cargando…</td>
								</tr>
							)
						}
					</tbody>
				</table>
			</div>
		);
	}

	render() {
		return (
			<div className="system-page backups-page">
				{ this.renderHeader() }
				{ this.renderSummary() }
				{ this.renderFilters() }
				{ this.renderTable() }
			</div>
		);
	}
}

export default Backups;
